import { Injectable } from '@angular/core';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { ListDownloadContainerComponent } from '@app/lists/shared/list-download-container/list-download-container.component';
import { UserService } from '@app/user/services/user.service';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { ListTypes } from '@shared/constants/lists-constants';
import { Button } from '@shared/models/ellipsis-option.model';
import { ProductSelectionData } from '@shared/models/usf-product-card.model';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { Customer } from '@usf/customer-types';
import {
  DownloadListOptions,
  ListState,
  ListTracingConstants,
  ListViewService,
  RECENT_PURCHASE_ACTIONS,
  searchInListTransformer,
  selectRecentPurchaseState,
} from '@usf/ngrx-list';
import {
  getLoadingStateOfProducts,
  LoadingState,
  selectProductDetails,
} from '@usf/ngrx-product';
import { UserKinds } from '@usf/user-types/user';
import {
  combineLatest,
  filter,
  firstValueFrom,
  iif,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { State } from 'src/app/store';
import { FEATURES } from '../../../../shared/constants/splitio-features';
import { mslProductLoadCheck } from '../../master-list/selectors/master-list.selectors';
import { RecentlyPurchasedViewModel } from '../model/recently-purchased-view.model';
import {
  isRPAndProductAndPricingDataLoaded,
  selectIfDaysAreWithinRecentPuchaseRange,
  selectRecentlyPurchasedProductNumbers,
  selectRecentlyPurchasedProductsLoaded,
  selectRpSelections,
  selectRpVm,
} from '../selectors/recently-purchased-view-model.selector';

@Injectable({
  providedIn: 'root',
})
export class RecentlyPurchasedService {
  private readonly _recentlyPurchasedDeleteFlag$ = this.panAppState.feature$([
    FEATURES.split_dynamic_rp_delete.name,
  ]);

  constructor(
    private store: Store<State>,
    private actionSheetCtrl: ActionSheetController,
    private popoverCtrl: PopoverController,
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    readonly panAppState: PanAppState,
    private listsOverlayService: ListsOverlayService,
    private loadingSpinnerService: LoadingSpinnerService,
    private readonly userService: UserService,
  ) {}

  public get recentlyPurchasedDelete$(): Observable<boolean> {
    return this._recentlyPurchasedDeleteFlag$;
  }

  recentlyPurchasedProductNumbers$ = () =>
    this.store.select(selectRecentlyPurchasedProductNumbers());

  isRPAndProductDataLoaded$ = (
    needDetails = false,
  ): Observable<LoadingState> => {
    return combineLatest([
      this.recentlyPurchasedProductNumbers$(),
      this.store.select(selectRecentPurchaseState),
      this.store.select(mslProductLoadCheck),
    ]).pipe(
      filter(
        ([productNumbers, recentPurchaseState, mslProductsAreLoaded]) =>
          recentPurchaseState?.loaded && mslProductsAreLoaded,
      ),
      switchMap(([productNumbers, recentPurchaseState]) =>
        combineLatest([
          this.store.select(getLoadingStateOfProducts(productNumbers)),
          iif(
            () => needDetails,
            this.store.select(
              selectProductDetails(productNumbers.filter(number => !!number)),
            ),
            of([{ loadingState: LoadingState.loaded }]),
          ),
        ]).pipe(
          map(([loadingState, details]) => {
            const isLoaded = this.isProductDetailsLoaded(details);
            let finalLoading = isLoaded ? loadingState : LoadingState.loading;
            return finalLoading;
          }),
        ),
      ),
    );
  };

  isProductDetailsLoaded = (productDetail: any[]) => {
    return productDetail.every(detail => {
      return (
        detail?.loadingState === LoadingState.loaded ||
        detail?.loadingState === LoadingState.notFound ||
        detail?.loadingState === LoadingState.error
      );
    });
  };

  isRPAndProductPricingDataLoaded$ = (
    needDetails = false,
  ): Observable<boolean> =>
    this.store.select(isRPAndProductAndPricingDataLoaded()).pipe(
      filter(values => !!values.isLoaded),
      switchMap(values =>
        combineLatest([
          of(values.isLoaded),
          iif(
            () => needDetails,
            this.store.select(
              selectProductDetails(
                values.productNumbers.filter(number => !!number),
              ),
            ),
            of([{ loadingState: LoadingState.loaded }]),
          ),
        ]).pipe(
          map(([loadingState, details]) => {
            const isLoaded = this.isProductDetailsLoaded(details);
            return isLoaded && loadingState;
          }),
        ),
      ),
    );

  selectRpVm$ = () => {
    return this.recentlyPurchasedProductNumbers$().pipe(
      switchMap((productNumbers: number[]) =>
        combineLatest([
          this.panAppState.feature$([FEATURES.split_global_min_order_met.name]),
          this.store.select(
            selectRpVm(productNumbers, this.platformService.platformType),
          ),
          this.isRPAndProductDataLoaded$(),
          this.panAppState.feature$([FEATURES.split_dynamic_pme_list.name]),
          this.userService.userKind$(),
        ]),
      ),
      map(
        ([
          orderMinimumMetFeatureFlag,
          rpViewModel,
          productLoadingState,
          pmeListFeatureFlag,
          userKind,
        ]) => {
          return {
            ...rpViewModel,
            orderMinimumMetFeatureFlag,
            productLoadingState,
            pmeListFeatureFlag:
              pmeListFeatureFlag && userKind === UserKinds.Internal,
          } as RecentlyPurchasedViewModel;
        },
      ),
    );
  };

  recentlyPurchasedProductsLoaded$ = (): Observable<boolean> =>
    this.store.select(selectRecentlyPurchasedProductsLoaded());

  daysThatAreWithinRecentPuchaseRange$ = (): Observable<boolean[]> =>
    this.store.select(selectIfDaysAreWithinRecentPuchaseRange());

  searchInRecentPurchase(
    searchKey: string,
    isStartOfTrace: boolean,
    isEndOfTrace: boolean,
  ) {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.searchInRecentPurchase({
        searchKey,
        tracking: {
          tracing: {
            data: {
              traceContext: ListTracingConstants.searchInList,
              isEndOfTrace,
              isStartOfTrace,
              attributes: {
                event: ListTracingConstants.searchWithinListEvent,
                spanName: ListTracingConstants.searchWithinListSpan,
                searchKey,
                searchType: ListTracingConstants.searchWithin,
                listId: '',
                listType: ListTracingConstants.recentPurchaseListType,
              },
            },
            transformFunc: searchInListTransformer,
          },
        },
      }),
    );
  }

  sortAndFilterInRecentPurchase(
    sortType: string,
    groupToSort: string,
    filters: string[],
    week52Filter: boolean,
  ) {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.sortAndFilterRecentPurchase({
        sortType,
        groupToSort,
        filters,
        week52Filter,
      }),
    );
  }

  openListOptions(
    event: Event,
    selectedCustomer: Customer,
    showDownloadAndPrint: boolean,
    listDownloadModal?: ListDownloadContainerComponent,
  ) {
    event.stopPropagation();
    const buttons = this.constructBannerButtons(
      selectedCustomer,
      showDownloadAndPrint,
      listDownloadModal,
    );
    this.presentBannerOptions(event, buttons);
  }

  private constructBannerButtons(
    selectedCustomer: Customer,
    showDownloadAndPrint?: boolean,
    listDownloadModal?: ListDownloadContainerComponent,
  ): Button[] {
    const buttons: Button[] = [];
    if (showDownloadAndPrint) {
      buttons.push(
        {
          text: this.translateService.instant('i18n.common.download'),
          handler: () => {
            this.listsOverlayService.openDownloadListModal(
              0,
              ListTypes.recentlyPurchased,
              'Recently Purchased',
              listDownloadModal,
            );

            this.dismissOptions();
          },
        },
        {
          text: this.translateService.instant('i18n.lists.print'),
          handler: () => {
            this.listsOverlayService.openPrintListModal(
              0,
              ListTypes.recentlyPurchased,
              'Recently Purchased',
              selectedCustomer.customerNumber,
              '',
            );
            this.dismissOptions();
          },
        },
      );
    }

    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(flagSet => {
        if (flagSet) {
          buttons.push({
            text: this.translateService.instant('i18n.lists.copy'),
            handler: () => {
              this.listsOverlayService.openCopyListModal({
                listName: 'Recently Purchased',
              } as ListState);
            },
          });
        }
      });

    return buttons;
  }

  private async presentBannerOptions(event: any, buttons: any[]) {
    if (!this.platformService.isTouch.value) {
      const listOptionsPopover = await this.popoverCtrl.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'ellipsis-options-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await listOptionsPopover.present();
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const listOptionsActionSheet = await this.actionSheetCtrl.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await listOptionsActionSheet.present();
    }
  }

  async dismissOptions() {
    if (this.platformService.isTouch.value) {
      await this.actionSheetCtrl.dismiss();
    } else {
      await this.popoverCtrl.dismiss();
    }
  }

  downloadList = (downloadListOptions: DownloadListOptions) => {
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.store.dispatch(
        RECENT_PURCHASE_ACTIONS.downloadList({ downloadListOptions }),
      );
    });
  };

  toggleAddToList = (value: boolean) => {
    this.store.dispatch(RECENT_PURCHASE_ACTIONS.selectAList({ value }));
  };

  selectAProduct = (selection: ProductSelectionData) => {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForARecentPurchase({
        key: selection.productNumber,
        value: selection.value,
      }),
    );
  };

  toggleSelectionForAllFilteredRecentPurchases = (
    value: boolean,
    keys: number[],
  ) => {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForMultipleRecentPurchases({
        keys,
        value,
      }),
    );
  };

  toggleSelectionsForAllRecentPurchase = (value: boolean) => {
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.setIsSelectedForAllRecentPurchases({ value }),
    );
  };

  getSelectedProducts = () => {
    return this.store.select(selectRpSelections());
  };

  deleteSingleProduct = async () => {
    const product = (
      await firstValueFrom(
        this.listsOverlayService.recentlyPurchasedDeleteModalOptions$,
      )
    ).product;
    this.store.dispatch(
      RECENT_PURCHASE_ACTIONS.delete({ products: [product] }),
    );
  };
}
